document.documentElement.classList.add('js-ready');

document.addEventListener('DOMContentLoaded', () => {
  const config = window.IndianRoots || {};
  const routes = config.routes || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const format = config.moneyFormat || '${{amount}}';
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(Number(cents || 0) / 100).toLocaleString('en-US'))
      .replace('{{amount_with_comma_separator}}', amount.replace(',', ' ').replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(Number(cents || 0) / 100).toLocaleString('de-DE'));
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const revealElements = document.querySelectorAll('.rvl');
  if ('IntersectionObserver' in window && !reducedMotion) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    revealElements.forEach((element) => observer.observe(element));
  } else {
    revealElements.forEach((element) => element.classList.add('is-visible'));
  }

  const createOverlay = ({ root, openButtons, closeButtons, bodyClass, focusSelector }) => {
    if (!root) return { open: () => {}, close: () => {} };
    let returnFocus = null;

    const open = (trigger) => {
      returnFocus = trigger || document.activeElement;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add(bodyClass);
      window.setTimeout(() => {
        const focusTarget = root.querySelector(focusSelector || 'button, a, input');
        if (focusTarget) focusTarget.focus();
      }, 80);
    };

    const close = () => {
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(bodyClass);
      if (returnFocus instanceof HTMLElement) returnFocus.focus();
    };

    openButtons.forEach((button) => button.addEventListener('click', () => open(button)));
    closeButtons.forEach((button) => button.addEventListener('click', close));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    return { open, close };
  };

  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const mobileMenuController = createOverlay({
    root: mobileMenu,
    openButtons: document.querySelectorAll('[data-mobile-menu-open]'),
    closeButtons: document.querySelectorAll('[data-mobile-menu-close]'),
    bodyClass: 'menu-open',
    focusSelector: '[data-mobile-menu-close]'
  });

  document.querySelectorAll('[data-mobile-submenu]').forEach((button) => {
    button.addEventListener('click', () => {
      const item = button.closest('.ir-mobile-menu__item');
      const isOpen = item.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(isOpen));
    });
  });

  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchController = createOverlay({
    root: searchOverlay,
    openButtons: document.querySelectorAll('[data-search-open]'),
    closeButtons: document.querySelectorAll('[data-search-close]'),
    bodyClass: 'search-open',
    focusSelector: 'input[type="search"]'
  });

  document.querySelectorAll('[data-search-open]').forEach((button) => {
    button.addEventListener('click', () => mobileMenuController.close());
  });

  const filterDrawer = document.querySelector('[data-filter-drawer]');
  createOverlay({
    root: filterDrawer,
    openButtons: document.querySelectorAll('[data-filter-open]'),
    closeButtons: document.querySelectorAll('[data-filter-close]'),
    bodyClass: 'filter-open',
    focusSelector: '[data-filter-close]'
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    });
  });

  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartController = createOverlay({
    root: cartDrawer,
    openButtons: document.querySelectorAll('[data-cart-open]'),
    closeButtons: document.querySelectorAll('[data-cart-close]'),
    bodyClass: 'drawer-open',
    focusSelector: '[data-cart-close]'
  });

  const cartItems = cartDrawer?.querySelector('[data-cart-items]');
  const cartFooter = cartDrawer?.querySelector('[data-cart-footer]');
  const cartTotal = cartDrawer?.querySelector('[data-cart-total]');

  const cartImage = (item) => {
    if (!item.image) return '';
    const url = new URL(item.image, window.location.origin);
    url.searchParams.set('width', '240');
    return `<img src="${escapeHtml(url.toString())}" alt="${escapeHtml(item.product_title)}" loading="lazy">`;
  };

  const renderCart = (cart) => {
    document.querySelectorAll('[data-header-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
    });
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = `(${cart.item_count})`;
    });

    if (!cartItems) return;
    if (cart.item_count === 0) {
      cartItems.innerHTML = `
        <div class="ir-cart-empty">
          <p>Your bag is ready for something beautiful.</p>
          <a class="ir-link-arrow" href="${escapeHtml(routes.allProducts || '/collections/all')}">
            <span>Explore the collection</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>`;
      if (cartFooter) cartFooter.hidden = true;
      return;
    }

    cartItems.innerHTML = cart.items
      .map(
        (item) => `
          <article class="ir-cart-line" data-cart-line data-key="${escapeHtml(item.key)}">
            <a class="ir-cart-line__image" href="${escapeHtml(item.url)}">${cartImage(item)}</a>
            <div class="ir-cart-line__info">
              <div>
                <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
                ${item.product_has_only_default_variant ? '' : `<p>${escapeHtml(item.variant_title || '')}</p>`}
              </div>
              <span>${formatMoney(item.final_line_price)}</span>
              <div class="ir-cart-line__controls">
                <div class="ir-stepper">
                  <button type="button" data-cart-quantity="-1" aria-label="Decrease quantity">−</button>
                  <span>${item.quantity}</span>
                  <button type="button" data-cart-quantity="1" aria-label="Increase quantity">+</button>
                </div>
                <button class="ir-cart-line__remove" type="button" data-cart-remove aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
              </div>
            </div>
          </article>`
      )
      .join('');

    if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price);
    if (cartFooter) cartFooter.hidden = false;
  };

  const fetchCart = async () => {
    const response = await fetch(routes.cartJson || '/cart.js', {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load your bag.');
    return response.json();
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    });
    if (!response.ok) throw new Error('Unable to update your bag.');
    return response.json();
  };

  cartItems?.addEventListener('click', async (event) => {
    const quantityButton = event.target.closest('[data-cart-quantity]');
    const removeButton = event.target.closest('[data-cart-remove]');
    if (!quantityButton && !removeButton) return;

    const line = event.target.closest('[data-cart-line]');
    const key = line?.dataset.key;
    if (!key) return;

    const current = Number(line.querySelector('.ir-stepper span')?.textContent || 1);
    const nextQuantity = removeButton ? 0 : current + Number(quantityButton.dataset.cartQuantity);

    try {
      line.style.opacity = '0.5';
      const cart = await changeCartLine(key, nextQuantity);
      renderCart(cart);
    } catch (error) {
      line.style.opacity = '';
      showToast(error.message);
    }
  });

  const initProduct = (root) => {
    const gallery = root.querySelector('[data-product-gallery]');
    const stage = root.querySelector('[data-product-stage]');
    const image = root.querySelector('[data-product-image]');
    const thumbs = Array.from(root.querySelectorAll('[data-product-thumb]'));
    const count = root.querySelector('[data-gallery-count]');
    let galleryIndex = Math.max(
      0,
      thumbs.findIndex((thumb) => thumb.classList.contains('is-active'))
    );

    const setGalleryImage = (index, shouldFocus = false) => {
      if (!image || thumbs.length === 0) return;
      galleryIndex = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[galleryIndex];
      image.classList.add('is-changing');

      window.setTimeout(() => {
        image.src = thumb.dataset.src;
        if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
        image.alt = thumb.dataset.alt || '';
        image.classList.remove('is-changing');
      }, reducedMotion ? 0 : 130);

      thumbs.forEach((item, itemIndex) => {
        const active = itemIndex === galleryIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      if (count) count.textContent = `${galleryIndex + 1} / ${thumbs.length}`;
      if (shouldFocus) stage?.focus();
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setGalleryImage(index));
    });
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => setGalleryImage(galleryIndex - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => setGalleryImage(galleryIndex + 1));
    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') setGalleryImage(galleryIndex - 1);
      if (event.key === 'ArrowRight') setGalleryImage(galleryIndex + 1);
    });

    let touchStart = null;
    stage?.addEventListener(
      'touchstart',
      (event) => {
        touchStart = event.touches[0]?.clientX ?? null;
      },
      { passive: true }
    );
    stage?.addEventListener(
      'touchend',
      (event) => {
        if (touchStart === null) return;
        const distance = (event.changedTouches[0]?.clientX ?? touchStart) - touchStart;
        if (Math.abs(distance) > 45) setGalleryImage(galleryIndex + (distance < 0 ? 1 : -1));
        touchStart = null;
      },
      { passive: true }
    );

    const variantsScript = root.querySelector('[data-product-variants]');
    const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    const select = root.querySelector('[data-variant-select]');
    const optionGroups = Array.from(root.querySelectorAll('[data-product-option]'));
    const hiddenOptions = Array.from(root.querySelectorAll('[data-hidden-option]'));
    const price = root.querySelector('[data-product-price]');
    const compare = root.querySelector('[data-product-compare]');
    const stock = root.querySelector('[data-product-stock]');
    const addButton = root.querySelector('[data-product-add]');
    const addLabel = root.querySelector('[data-product-add-label]');
    const buttonPrice = root.querySelector('[data-product-button-price]');

    const selectedOptions = () => {
      const values = [];
      hiddenOptions.forEach((input) => {
        values[Number(input.dataset.optionPosition) - 1] = input.value;
      });
      optionGroups.forEach((group) => {
        const checked = group.querySelector('input:checked');
        values[Number(group.dataset.optionPosition) - 1] = checked?.value;
      });
      return values;
    };

    const updateOptionAvailability = (selected) => {
      optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const possible = variants.some((variant) => {
            if (!variant.available || variant.options[position] !== input.value) return false;
            return selected.every((value, optionIndex) => optionIndex === position || !value || variant.options[optionIndex] === value);
          });
          input.disabled = !possible;
          input.nextElementSibling?.classList.toggle('is-unavailable', !possible);
        });
      });
    };

    const updateVariant = () => {
      const selected = selectedOptions();
      const variant = variants.find(
        (item) => item.options.length === selected.length && item.options.every((value, index) => value === selected[index])
      );
      if (!variant) return;

      if (select) select.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);
      if (buttonPrice) buttonPrice.textContent = formatMoney(variant.price);
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = formatMoney(variant.compare_at_price);
      }
      if (stock) {
        stock.textContent = variant.available ? 'Ready to ship' : 'Sold out';
        stock.classList.toggle('is-out', !variant.available);
      }
      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';

      optionGroups.forEach((group) => {
        const checked = group.querySelector('input:checked');
        const current = group.querySelector('[data-option-current]');
        if (current && checked) current.textContent = checked.value;
      });
      updateOptionAvailability(selected);

      if (variant.featured_image) {
        const matchingIndex = thumbs.findIndex((thumb) => Number(thumb.dataset.imageId) === Number(variant.featured_image.id));
        if (matchingIndex >= 0) setGalleryImage(matchingIndex);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionGroups.forEach((group) => group.addEventListener('change', updateVariant));
    if (variants.length) updateOptionAvailability(selectedOptions());

    root.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const input = root.querySelector('[data-quantity-input]');
        if (!input) return;
        input.value = Math.max(1, Number(input.value || 1) + Number(button.dataset.quantityChange));
      });
    });

    const productForm = root.querySelector('[data-product-form]');
    productForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!addButton || addButton.disabled) return;

      const originalLabel = addLabel?.textContent;
      addButton.disabled = true;
      if (addLabel) addLabel.textContent = 'Adding...';

      try {
        const response = await fetch(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(productForm)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This piece could not be added.');
        const cart = await fetchCart();
        renderCart(cart);
        cartController.open(addButton);
        showToast('Added to your bag.');
      } catch (error) {
        showToast(error.message);
      } finally {
        const selectedVariant = variants.find((variant) => String(variant.id) === String(select?.value));
        addButton.disabled = selectedVariant ? !selectedVariant.available : false;
        if (addLabel) {
          addLabel.textContent = selectedVariant && !selectedVariant.available ? 'Sold out' : originalLabel;
        }
      }
    });
  };

  document.querySelectorAll('[data-product-root]').forEach(initProduct);
});
